import { FunctionComponent, useEffect, useState } from 'react';
import { MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    getProjectThirdPartyExpenses,
    addProjectThirdPartyExpense,
    deleteProjectThirdPartyExpense
} from 'src/api/apiCaller';
import { IThirdPartyExpense, ThirdPartyProvider } from 'src/types/projects';
import { ClientSelectors } from 'src/redux/client/selectors';
import {
    Container,
    SectionTitle,
    ChipsContainer,
    ExpenseChip,
    FormRow,
    ProviderSelect,
    AccountIdInput,
    AddButton
} from './styled';

const providerLabels: Record<number, string> = {
    [ThirdPartyProvider.AWS]: 'AWS'
};

const ThirdPartyExpenses: FunctionComponent = () => {
    const { selectedProjectId } = ClientSelectors();
    const [expenses, setExpenses] = useState<IThirdPartyExpense[]>([]);
    const [accountId, setAccountId] = useState('');
    const [provider, setProvider] = useState<number>(ThirdPartyProvider.AWS);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!selectedProjectId) return;
        const fetchExpenses = async () => {
            try {
                const response =
                    await getProjectThirdPartyExpenses(selectedProjectId);
                setExpenses(response.expenses || []);
            } catch (err) {
                console.error('Failed to fetch third-party expenses', err);
            }
        };
        fetchExpenses();
    }, [selectedProjectId]);

    const handleAdd = async () => {
        if (!selectedProjectId || !accountId.trim()) return;
        setLoading(true);
        try {
            const response = await addProjectThirdPartyExpense(
                selectedProjectId,
                accountId.trim(),
                provider
            );
            setExpenses(response.expenses || []);
            setAccountId('');
        } catch (err) {
            console.error('Failed to add third-party expense', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (expenseId: number) => {
        if (!selectedProjectId) return;
        try {
            await deleteProjectThirdPartyExpense(selectedProjectId, expenseId);
            setExpenses((prev) => prev.filter((e) => e.id !== expenseId));
        } catch (err) {
            console.error('Failed to delete third-party expense', err);
        }
    };

    return (
        <Container>
            <SectionTitle>Third-Party Expenses</SectionTitle>

            {expenses.length > 0 && (
                <ChipsContainer>
                    {expenses.map((expense) => (
                        <ExpenseChip
                            key={expense.id}
                            label={`${providerLabels[expense.provider] || 'Unknown'}: ${expense.accountId}`}
                            onDelete={() => handleDelete(expense.id)}
                            deleteIcon={<CloseIcon />}
                        />
                    ))}
                </ChipsContainer>
            )}

            <FormRow>
                <ProviderSelect
                    value={provider}
                    onChange={(e) => setProvider(Number(e.target.value))}
                    size="small"
                >
                    <MenuItem value={ThirdPartyProvider.AWS}>AWS</MenuItem>
                </ProviderSelect>

                <AccountIdInput
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                    placeholder="Account ID"
                    size="small"
                />

                <AddButton
                    onClick={handleAdd}
                    disabled={loading || !accountId.trim()}
                    size="small"
                >
                    Add
                </AddButton>
            </FormRow>
        </Container>
    );
};

export default ThirdPartyExpenses;
